import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUser } from '@/queries/useUser';
import { profileFormSchema, type ProfileFormValues } from '../validations/profileSchema';
import type { SocialPlatform } from '../types/profile.types';
import { extractUsernameFromUrl, getSocialPlatformConfig } from '../constants/socialPlatforms';
import { stanNotify } from '@/lib/stanNotify';
import { useUpdateProfile } from '../queries';
import { isAxiosError } from 'axios';
import { useStanRouter } from '@/hooks/useStanRouter';

function getUserSocialLinks(
    user: { data?: { socials?: Record<string, string> } } | null
): Record<SocialPlatform, string> {
    const defaultLinks: Record<SocialPlatform, string> = {
        instagram: '',
        tiktok: '',
        email: '',
        facebook: '',
        youtube: '',
        link: '',
        pinterest: '',
        twitter: '',
        spotify: '',
        'apple-podcasts': '',
        etsy: '',
        discord: '',
        snapchat: '',
        twitch: '',
        vimeo: '',
        linkedin: '',
    };

    if (!user?.data?.socials) return defaultLinks;

    // Merge user's social links with defaults
    const processedLinks = Object.fromEntries(
        Object.entries(user.data.socials)
            .filter(([_, url]) => typeof url === 'string')
            .map(([platform, url]) => {
                const config = getSocialPlatformConfig(platform as SocialPlatform);

                // Username-based platforms are already stored as usernames, not URLs
                if (config && config.inputType === 'username') {
                    return [platform, url];
                }

                // For URL-based platforms, extract username if needed
                return [platform, extractUsernameFromUrl(platform as SocialPlatform, url as string)];
            })
    );

    return {
        ...defaultLinks,
        ...processedLinks,
    } as Record<SocialPlatform, string>;
}

interface UseProfileFormOptions {
    redirectTo?: string;
    onSuccess?: () => void;
}

export function useProfileForm(options?: UseProfileFormOptions) {
    const { user } = useUser();
    const { mutateAsync: updateProfileMutation, isPending } = useUpdateProfile();
    const { push } = useStanRouter();

    const { redirectTo = '/dashboard', onSuccess } = options || {};

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(profileFormSchema),
        defaultValues: {
            displayName: '',
            bio: '',
            socialLinks: {
                instagram: '',
                tiktok: '',
                email: '',
                facebook: '',
                youtube: '',
                link: '',
                pinterest: '',
                twitter: '',
                spotify: '',
                'apple-podcasts': '',
                etsy: '',
                discord: '',
                snapchat: '',
                twitch: '',
                vimeo: '',
                linkedin: '',
            },
        },
        mode: 'onChange',
    });

    const { reset, handleSubmit, formState } = form;
    const { isDirty } = formState;

    const resetForm = useCallback(() => {
        if (user) {
            reset({
                displayName: user.full_name || '',
                bio: user.data?.bio || '',
                socialLinks: getUserSocialLinks(user),
            });
        }
    }, [user, reset]);

    // Initialize form data from user only on mount or when form is not dirty
    useEffect(() => {
        // Only reset if form is not dirty (no unsaved changes)
        if (!isDirty) {
            resetForm();
        }
    }, [resetForm, isDirty]);

    const onSubmitHandler = useCallback(
        async (data: ProfileFormValues) => {
            try {
                await updateProfileMutation({ data });

                stanNotify({
                    type: 'success',
                    title: 'Profile Updated',
                    text: 'Your profile has been updated successfully',
                    duration: 3000,
                });

                reset(data);

                if (onSuccess) {
                    onSuccess();
                }

                push(redirectTo);
            } catch (error) {
                let errorMessage = 'An error occurred while updating your profile. Please try again.';

                if (isAxiosError(error)) {
                    errorMessage = error.response?.data?.message || error.message;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                stanNotify({
                    type: 'error',
                    title: 'Update Failed',
                    text: errorMessage,
                    duration: 5000,
                });
            }
        },
        [updateProfileMutation, reset, push, redirectTo, onSuccess]
    );

    const onInvalidHandler = useCallback((errors: any) => {
        console.error('Form validation errors:', errors);

        // Find the first error message to display
        const firstError = Object.values(errors).find((error: any) => error?.message);
        const errorMessage = (firstError as any)?.message || 'Please fix the validation errors before submitting.';

        stanNotify({
            type: 'error',
            title: 'Validation Error',
            text: errorMessage,
            duration: 5000,
        });
    }, []);

    const onSubmit = handleSubmit(onSubmitHandler, onInvalidHandler);

    return {
        form,
        onSubmit,
        resetForm,
        isDirty,
        isSubmitting: isPending,
    };
}
